import { FormField, Numeric, Select } from "@rothoblaas/design-system";
import { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import FormFieldsContainer from "../../../components/ui/FormFieldsContainer";
import useI18n from "../../../hooks/useTranslations";
import { LoadDuration } from "../../../http/beamReinforcement/types/loadDuration";
import { BeamReinforcementConfigurationFormValues } from "../../../http/beamReinforcement/useAutoSaveBeamReinforcementConfiguration";

type ServiceClassOption = { value: 1 | 2 | 3; label: string };
type LoadDurationOption = { value: LoadDuration; label: string };

type GeneralDataCardProps = {
  defaultValues: BeamReinforcementConfigurationFormValues;
  disabled?: boolean;
};

const GeneralDataCard: FC<GeneralDataCardProps> = ({ defaultValues, disabled = false }) => {
  const i18n = useI18n();
  const t = i18n.translation.beamReinforcementModule.calculation;
  const {
    control,
    formState: { errors },
  } = useFormContext<BeamReinforcementConfigurationFormValues>();

  const SERVICE_CLASS_OPTIONS: ServiceClassOption[] = [
    { value: 1, label: t.serviceClass1 },
    { value: 2, label: t.serviceClass2 },
    { value: 3, label: t.serviceClass3 },
  ];

  const LOAD_DURATION_OPTIONS: LoadDurationOption[] = [
    { value: "perm", label: t.loadDurationPerm },
    { value: "long", label: t.loadDurationLong },
    { value: "medium", label: t.loadDurationMedium },
    { value: "short", label: t.loadDurationShort },
    { value: "inst", label: t.loadDurationInst },
  ];

  return (
    <div>
      <FormFieldsContainer fullWidth>
        <Controller
          name="serviceClass"
          control={control}
          defaultValue={defaultValues.serviceClass}
          rules={{ required: true }}
          render={({ field }) => (
            <FormField label={t.serviceClass} error={errors.serviceClass}>
              {({ labelId, isInvalid }) => (
                <Select<ServiceClassOption, false>
                  {...field}
                  inputId={labelId}
                  isDisabled={disabled}
                  value={SERVICE_CLASS_OPTIONS.find((option) => option.value === field.value) ?? null}
                  options={SERVICE_CLASS_OPTIONS}
                  getOptionValue={(option) => `${option.value}`}
                  getOptionLabel={(option) => option.label}
                  isInvalid={isInvalid}
                  onChange={(selectedOption) => field.onChange(selectedOption?.value ?? null)}
                />
              )}
            </FormField>
          )}
        />

        <Controller
          name="loadDuration"
          control={control}
          defaultValue={defaultValues.loadDuration}
          rules={{ required: true }}
          render={({ field }) => (
            <FormField label={t.loadDuration} error={errors.loadDuration}>
              {({ labelId, isInvalid }) => (
                <Select<LoadDurationOption, false>
                  {...field}
                  inputId={labelId}
                  isDisabled={disabled}
                  value={LOAD_DURATION_OPTIONS.find((option) => option.value === field.value) ?? null}
                  options={LOAD_DURATION_OPTIONS}
                  getOptionValue={(option) => option.value}
                  getOptionLabel={(option) => option.label}
                  isInvalid={isInvalid}
                  onChange={(selectedOption) => field.onChange(selectedOption?.value ?? null)}
                />
              )}
            </FormField>
          )}
        />

        <Controller
          name="tipTolerance"
          control={control}
          defaultValue={defaultValues.tipTolerance}
          rules={{ min: 0 }}
          render={({ field }) => (
            <FormField label={t.tipTolerance} error={errors.tipTolerance}>
              {({ labelId, isInvalid }) => (
                <Numeric
                  {...field}
                  id={labelId}
                  value={field.value}
                  suffix={` ${i18n.translation.common.unitMm}`}
                  decimalScale={0}
                  step={1}
                  min={0}
                  disabled={disabled}
                  allowNegative={false}
                  isInvalid={isInvalid}
                  onValueChange={(value) => field.onChange(value)}
                />
              )}
            </FormField>
          )}
        />

        <Controller
          name="permanentLoad"
          control={control}
          defaultValue={defaultValues.permanentLoad}
          rules={{ min: 0 }}
          render={({ field }) => (
            <FormField label={t.permanentLoad} error={errors.permanentLoad}>
              {({ labelId, isInvalid }) => (
                <Numeric
                  {...field}
                  id={labelId}
                  value={field.value}
                  suffix={` ${i18n.translation.common.unitKnM}`}
                  decimalScale={2}
                  step={0.1}
                  min={0}
                  disabled={disabled}
                  allowNegative={false}
                  isInvalid={isInvalid}
                  onValueChange={(value) => field.onChange(value)}
                />
              )}
            </FormField>
          )}
        />

        <Controller
          name="liveLoad"
          control={control}
          defaultValue={defaultValues.liveLoad}
          rules={{ min: 0 }}
          render={({ field }) => (
            <FormField label={t.liveLoad} error={errors.liveLoad}>
              {({ labelId, isInvalid }) => (
                <Numeric
                  {...field}
                  id={labelId}
                  value={field.value}
                  suffix={` ${i18n.translation.common.unitKnM}`}
                  decimalScale={2}
                  step={0.1}
                  min={0}
                  disabled={disabled}
                  allowNegative={false}
                  isInvalid={isInvalid}
                  onValueChange={(value) => field.onChange(value)}
                />
              )}
            </FormField>
          )}
        />

        <Controller
          name="beamLength"
          control={control}
          defaultValue={defaultValues.beamLength}
          rules={{ min: 0.5 }}
          render={({ field }) => (
            <FormField label={t.beamLength} error={errors.beamLength}>
              {({ labelId, isInvalid }) => (
                <Numeric
                  {...field}
                  id={labelId}
                  value={field.value}
                  suffix={` ${i18n.translation.common.unitM}`}
                  decimalScale={2}
                  step={0.1}
                  min={0.5}
                  disabled={disabled}
                  allowNegative={false}
                  isInvalid={isInvalid}
                  onValueChange={(value) => field.onChange(value)}
                />
              )}
            </FormField>
          )}
        />
      </FormFieldsContainer>
    </div>
  );
};

export default GeneralDataCard;
